import { ClipboardDocumentIcon, EllipsisVerticalIcon, EyeIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid'
import { FolderPlusIcon } from '@heroicons/react/24/outline'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useParams } from 'react-router'
import { Head } from '#/components/head'
import { EmptyState } from '#/components/empty-state'
import { Button } from '#/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '#/components/ui/dropdown-menu'
import { Separator } from '#/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '#/components/ui/table'
import { Heading, Subheading, Text } from '#/components/ui/typography'
import { copy } from '#/lib/copy'
import { formatDateTime2Digit } from '#/lib/datetime'
import { stickerAPI } from '#/api/sticker'
import { DeleteStickerModal } from './@modal/delete/page'
import type { Sticker } from 'cleverev-types'
import { toast } from 'sonner'


export default function StickersPage() {
  const { t, i18n } = useTranslation([ 'stickers' ])
  const queryClient = useQueryClient()
  const params = useParams() as { organization: string }
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState<{ stickerId: string } | null>(null)

  // Fetch stickers
  const { data: { stickers, totalCount } } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'stickers' ],
    queryFn: () => stickerAPI.listStickers(params.organization),
    select: data => {
      data.stickers.forEach(stickerData => {
        queryClient.setQueryData<Sticker>(
          [ 'organizations', params.organization, 'partners', stickerData.id ],
          stickerData
        )
      })

      return data
    }
  })

  // Copy sticker id and notify
  function copyStickerId(sticker: Sticker) {
    copy(sticker.id)
    toast.info(t('view_sticker.id_copied'))
  }

  return (
    <div className="flex grow flex-col gap-4 items-start">

      <Head title={t('sticker_list.page_title')} />

      <Outlet />

      <div className="flex w-full items-end justify-between gap-4">
        <Heading>{t('sticker_list.header_title')}</Heading>
        <Link to="./new">
          <Button>
            <PlusIcon />
            {t('sticker_list.action_new_sticker')}
          </Button>
        </Link>
      </div>

      <Text>{t('sticker_list.header_description')}</Text>

      <Separator />

      {totalCount === 0 ? (
        <EmptyState
          title={t('sticker_list.empty_title')}
          description={t('sticker_list.empty_description')}
          Icon={FolderPlusIcon}
        >
          <Link to="./new">
            <Button>
              <PlusIcon />
              {t('sticker_list.action_new_sticker')}
            </Button>
          </Link>
        </EmptyState>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('fields.id')}</TableHead>
                <TableHead>{t('fields.chargerId')}</TableHead>
                <TableHead>{t('fields.connectorId')}</TableHead>
                <TableHead>{t('fields.created_at')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isDeleteModalOpen !== null
              && (<DeleteStickerModal
                id={isDeleteModalOpen.stickerId}
                onClose={() => setIsDeleteModalOpen(null)}
              />)}
              {stickers.map(stickerData => (
                <TableRow key={stickerData.id}>
                  <TableCell className="font-medium">
                    <Link to={`./${stickerData.id}`} state={{ name: stickerData.id }}>
                      <Subheading>{stickerData.id}</Subheading>
                    </Link>
                  </TableCell>
                  <TableCell>
                    {stickerData.chargerId}
                  </TableCell>
                  <TableCell>
                    {stickerData.connectorId}
                  </TableCell>
                  <TableCell>
                    {formatDateTime2Digit(stickerData.createdAt, i18n.language)}
                  </TableCell>
                  <TableCell className="text-right flex flex-row justify-end">
                    <Link to={`./${stickerData.id}`} state={{ name: stickerData.id }}>
                      <Button variant="ghost" className="mr-4">
                        {t('sticker_list.view_button')}
                      </Button>
                    </Link>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <EllipsisVerticalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuLabel>{t('sticker_list.menu.label')}</DropdownMenuLabel>
                        <Link to={`./${stickerData.id}`} state={{ name: stickerData.id }}>
                          <DropdownMenuItem>
                            <EyeIcon className="text-tertiary" />
                            {t('sticker_list.menu.view')}
                          </DropdownMenuItem>
                        </Link>
                        <DropdownMenuItem onClick={() => copyStickerId(stickerData)}>
                          <ClipboardDocumentIcon className="text-tertiary" />
                          {t('sticker_list.menu.copy_id')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() =>
                          setIsDeleteModalOpen({
                            stickerId: stickerData.id
                            // name: stickerData.sticker.id
                          })}>
                          <TrashIcon className="text-tertiary" />
                          {t('sticker_list.menu.remove')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

    </div>
  )
}
